import { useState } from "react";
import { Plus, Send, CheckCircle2, MessageSquare, BookTemplate } from "lucide-react";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Tag,
  message,
  Tabs,
  Table,
  Popconfirm,
  Drawer,
  Typography,
  Space,
  Alert,
} from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useRef, useEffect } from "react";
import { IThread, IMessage } from "../../../types/api/messaging";
import { useThreadList, useThreadMessages, useCreateThread, useSendMessage, useResolveThread } from "../../../hooks/api/messages/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";
import {
  useCommsTemplates,
  useCreateCommsTemplate,
  useUpdateCommsTemplate,
  useDeleteCommsTemplate,
} from "../../../hooks/api/comms/hooks";

dayjs.extend(relativeTime);

const STATUS_COLOR: Record<string, string> = { open: "blue", pending: "orange", resolved: "green", closed: "default" };
const CHANNEL_COLOR: Record<string, string> = { email: "blue", sms: "green", whatsapp: "cyan", general: "default" };

// ── New Thread Modal ──────────────────────────────────────────────────────────

function NewThreadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const createThread = useCreateThread({
    onSuccess: () => { msgApi.success("Thread created."); onClose(); form.resetFields(); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="New Message Thread"
      width={480}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="send" type="primary" loading={createThread.isPending} onClick={() => form.submit()}>Send</Button>,
      ]}
    >
      {ctx}
      <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => createThread.mutate(vals)}>
        <Form.Item name="assigned_to" label="Send To (Worker)">
          <Select
            showSearch
            placeholder="Select worker…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={workers.map((w: any) => ({
              value: w.id,
              label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
            }))}
          />
        </Form.Item>
        <Form.Item name="subject" label="Subject">
          <Input placeholder="e.g. Job site update, PPE reminder" />
        </Form.Item>
        <Form.Item name="body" label="Message" rules={[{ required: true, message: "Message required" }]}>
          <Input.TextArea rows={4} placeholder="Type your message…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Chat Panel ────────────────────────────────────────────────────────────────

function ChatPanel({ thread, onResolve }: { thread: IThread; onResolve: () => void }) {
  const [body, setBody] = useState("");
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data: res, isLoading } = useThreadMessages(thread.id);
  const messages: IMessage[] = (res?.data as any)?.messages ?? [];

  const { data: templatesRes } = useCommsTemplates();
  const templates: any[] = templatesRes?.data ?? [];

  const send = useSendMessage({
    onSuccess: () => setBody(""),
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const resolve = useResolveThread({ onSuccess: onResolve });

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  return (
    <div className="flex flex-col h-full">
      {ctx}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-800 text-sm">{thread.subject || "No subject"}</div>
          <div className="text-xs text-slate-400">{(thread as any).assigned_to_name ? `To: ${(thread as any).assigned_to_name}` : "General thread"}</div>
        </div>
        {thread.status !== "resolved" && (
          <Button size="small" icon={<CheckCircle2 size={12} />} onClick={() => resolve.mutate(thread.id)} loading={resolve.isPending}>
            Resolve
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/40">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spin /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-10">No messages yet.</div>
        ) : (
          messages.map((m) => {
            const isOut = m.direction === "outbound";
            return (
              <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${isOut ? "text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-100 rounded-bl-sm"}`} style={isOut ? { background: "var(--color-primary)" } : {}}>
                  {!isOut && (m as any).sender_name && <div className="text-[10px] font-semibold mb-1 text-primary">{(m as any).sender_name}</div>}
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${isOut ? "text-blue-200" : "text-slate-400"}`}>
                    {dayjs(m.created_at).fromNow()}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {thread.status !== "resolved" && (
        <div className="px-4 py-3 border-t border-slate-100 bg-white space-y-2">
          {templates.length > 0 && (
            <div>
              <Button
                type="link"
                size="small"
                className="p-0 text-xs"
                onClick={() => setTemplateSelectOpen(!templateSelectOpen)}
              >
                Use Template
              </Button>
              {templateSelectOpen && (
                <Select
                  placeholder="Pick a template…"
                  style={{ width: "100%", marginTop: 4 }}
                  size="small"
                  onChange={(val) => {
                    const t = templates.find((tmpl: any) => tmpl.id === val);
                    if (t) { setBody(t.content); setTemplateSelectOpen(false); }
                  }}
                  allowClear
                >
                  {templates.map((t: any) => (
                    <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
                  ))}
                </Select>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Input.TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (body.trim()) send.mutate({ thread_id: thread.id, body: body.trim() }); } }}
              placeholder="Type a message… (Enter to send)"
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="flex-1"
            />
            <Button
              type="primary"
              icon={<Send size={14} />}
              loading={send.isPending}
              onClick={() => { if (body.trim()) send.mutate({ thread_id: thread.id, body: body.trim() }); }}
              disabled={!body.trim()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Templates Tab ─────────────────────────────────────────────────────────────

function TemplatesTab() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [detectedVars, setDetectedVars] = useState<string[]>([]);

  const { data: res, isLoading } = useCommsTemplates();
  const templates: any[] = res?.data ?? [];

  const { mutate: createTemplate, isPending: creating } = useCreateCommsTemplate();
  const { mutate: updateTemplate, isPending: updating } = useUpdateCommsTemplate();
  const { mutate: deleteTemplate } = useDeleteCommsTemplate();

  function detectVars(content: string) {
    const matches = content.match(/\{(\w+)\}/g) ?? [];
    setDetectedVars([...new Set(matches.map((m) => m.slice(1, -1)))]);
  }

  function openCreate() {
    setEditingTemplate(null);
    form.resetFields();
    setDetectedVars([]);
    setDrawerOpen(true);
  }

  function openEdit(tpl: any) {
    setEditingTemplate(tpl);
    form.setFieldsValue({ name: tpl.name, channel: tpl.channel, content: tpl.content });
    detectVars(tpl.content || "");
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditingTemplate(null);
    form.resetFields();
    setDetectedVars([]);
  }

  function onFinish(values: any) {
    const payload = { ...values, variables: detectedVars };
    if (editingTemplate) {
      updateTemplate(
        { id: editingTemplate.id, ...payload },
        { onSuccess: () => { message.success("Template updated."); closeDrawer(); }, onError: () => message.error("Failed to update template.") }
      );
    } else {
      createTemplate(
        payload,
        { onSuccess: () => { message.success("Template created."); closeDrawer(); }, onError: () => message.error("Failed to create template.") }
      );
    }
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (v: string) => <span className="font-medium text-slate-800">{v}</span>,
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (v: string) => <Tag color={CHANNEL_COLOR[v] ?? "default"}>{v}</Tag>,
    },
    {
      title: "Preview",
      dataIndex: "content",
      key: "content",
      render: (v: string) => <span className="text-slate-500 text-xs">{v?.slice(0, 70)}{v?.length > 70 ? "…" : ""}</span>,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => <span className="text-xs text-slate-400">{dayjs(v).format("DD MMM YYYY")}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this template?"
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => deleteTemplate(record.id, {
              onSuccess: () => message.success("Template deleted."),
              onError: () => message.error("Failed to delete."),
            })}
          >
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Typography.Text type="secondary" className="text-sm">
          Reusable message templates for quick sending
        </Typography.Text>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>New Template</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spin /></div>
      ) : (
        <Table
          rowKey="id"
          dataSource={templates}
          columns={columns}
          pagination={false}
          size="small"
        />
      )}

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editingTemplate ? "Edit Template" : "New Template"}
        width={500}
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" loading={creating || updating} onClick={() => form.submit()}>
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="name" label="Template Name" rules={[{ required: true, message: "Name required" }]}>
            <Input placeholder="e.g. Job Confirmation" />
          </Form.Item>
          <Form.Item name="channel" label="Channel" initialValue="general">
            <Select>
              <Select.Option value="general">General</Select.Option>
              <Select.Option value="email">Email</Select.Option>
              <Select.Option value="sms">SMS</Select.Option>
              <Select.Option value="whatsapp">WhatsApp</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="content"
            label="Content"
            rules={[{ required: true, message: "Content required" }]}
            extra={
              <span className="text-xs text-slate-400">
                Use {"{"} {"}"} for variables: {"{client_name}"}, {"{job_title}"}, {"{company_name}"}, {"{job_date}"}
              </span>
            }
          >
            <Input.TextArea
              rows={6}
              placeholder="Hi {client_name}, your job has been confirmed for {job_date}."
              onChange={(e) => detectVars(e.target.value)}
            />
          </Form.Item>
          {detectedVars.length > 0 && (
            <Alert
              type="info"
              message={
                <span className="text-xs">
                  Detected variables: {detectedVars.map((v) => <Tag key={v} className="text-xs">{v}</Tag>)}
                </span>
              }
            />
          )}
        </Form>
      </Drawer>
    </div>
  );
}

// ── Main CommsHub Page ────────────────────────────────────────────────────────

const CommsHubPage = () => {
  const [newOpen, setNewOpen] = useState(false);
  const [activeThread, setActiveThread] = useState<IThread | null>(null);

  const { data: res, isLoading, refetchWithQuery } = useThreadList({ page: 1, limit: 30 });
  const threads: IThread[] = (res?.data as any) ?? [];

  const messagingTab = (
    <div className="flex h-[calc(100vh-160px)] overflow-hidden rounded-lg border border-slate-100 bg-white">
      {/* sidebar */}
      <div className="w-72 flex-shrink-0 border-r border-slate-100 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <span className="font-semibold text-slate-800">Threads</span>
          <Button size="small" type="primary" icon={<Plus size={12} />} onClick={() => setNewOpen(true)}>
            New
          </Button>
        </div>
        <div className="px-3 py-2">
          <input
            type="text"
            placeholder="Search…"
            className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary"
            onChange={(e) => refetchWithQuery({ page: 1, search: e.target.value })}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spin size="small" /></div>
          ) : threads.length === 0 ? (
            <div className="text-center text-slate-400 text-xs py-8">No threads yet.</div>
          ) : (
            threads.map((t) => (
              <div
                key={t.id}
                onClick={() => setActiveThread(t)}
                className={`px-4 py-3 cursor-pointer border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeThread?.id === t.id ? "bg-slate-100 border-l-2 border-l-primary" : ""}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-slate-800 truncate">{t.subject || "No subject"}</span>
                  <Tag color={STATUS_COLOR[t.status]} className="text-[10px] ml-1 shrink-0">{t.status}</Tag>
                </div>
                {(t as any).assigned_to_name && <div className="text-xs text-slate-400 truncate">To: {(t as any).assigned_to_name}</div>}
                {(t as any).last_message && <div className="text-xs text-slate-500 truncate mt-0.5">{(t as any).last_message}</div>}
                <div className="text-[10px] text-slate-300 mt-1">{t.last_message_at ? dayjs(t.last_message_at).fromNow() : "—"}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* chat area */}
      <div className="flex-1 overflow-hidden">
        {activeThread ? (
          <ChatPanel thread={activeThread} onResolve={() => { setActiveThread(null); refetchWithQuery({ page: 1, limit: 30 }); }} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
            <MessageSquare size={40} className="opacity-30" />
            <p className="text-sm">Select a thread or start a new message</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 min-h-screen">
      <div className="mb-6">
        <h1 className="text-xl font-semibold">Comms Hub</h1>
        <p className="text-sm text-slate-500 mt-0.5">Messaging threads and reusable message templates</p>
      </div>

      <Tabs
        defaultActiveKey="messages"
        items={[
          {
            key: "messages",
            label: <span className="flex items-center gap-1"><MessageSquare size={14} />Messages</span>,
            children: messagingTab,
          },
          {
            key: "templates",
            label: <span className="flex items-center gap-1"><BookTemplate size={14} />Templates</span>,
            children: <TemplatesTab />,
          },
        ]}
      />

      <NewThreadModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
};

export default CommsHubPage;
